<script setup>
// Форма опитування для модератора: питання, варіанти, час.
//
// Відкривається поверх стрічки — там же, де налаштування профілю: писати
// питання й одночасно читати чат однаково не вийде, а вузька колонка іншого
// місця не має.
//
// Час — готовими кнопками, а не полем: у прямому ефірі опитування ставлять
// на пів хвилини чи хвилину, і набирати «60» руками ніхто не буде.

const emit = defineEmits(['create', 'close'])

// Стільки ж, скільки приймає сервер (див. backend/src/chat/polls.js): форма
// не має пропонувати того, що там відмовлять
const MAX_OPTIONS = 5

const DURATIONS = [
  { label: '30 с', seconds: 30 },
  { label: '1 хв', seconds: 60 },
  { label: '2 хв', seconds: 120 },
  { label: '5 хв', seconds: 300 }
]

const question = ref('')
// Два порожні поля одразу: менше двох варіантів опитування не буває, і
// тиснути «додати» заради очевидного не варто
const options = ref(['', ''])
const seconds = ref(60)
const failure = ref('')

const questionInput = ref(null)

onMounted(() => nextTick(() => questionInput.value?.focus()))

const addOption = () => {
  if (options.value.length >= MAX_OPTIONS) return
  options.value = [...options.value, '']
}

const dropOption = (index) => {
  if (options.value.length <= 2) return
  options.value = options.value.filter((_, i) => i !== index)
}

const filled = computed(() => options.value.map((o) => o.trim()).filter(Boolean))

const canSubmit = computed(() => !!question.value.trim() && filled.value.length >= 2)

const submit = () => {
  if (!canSubmit.value) {
    failure.value = 'Потрібні питання і щонайменше два варіанти'
    return
  }

  emit('create', {
    question: question.value.trim(),
    options: filled.value,
    seconds: seconds.value
  })

  emit('close')
}

// Enter у полях надсилає форму, а не переносить рядок: полів тут кілька, і
// шукати кнопку після кожного — зайвий шлях
const onKey = (event) => {
  if (event.key === 'Escape') {
    emit('close')
    return
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    submit()
  }
}
</script>

<template>
  <div class="z-30 absolute inset-0 flex flex-col bg-base-300">
    <div
      class="flex flex-shrink-0 items-center gap-2 bg-base-200 px-2 py-1.5 border-base-content/10 border-b"
    >
      <span class="font-semibold text-xs">Нове опитування</span>
      <button
        class="ml-auto text-base-content/50 hover:text-base-content"
        title="Закрити (Esc)"
        @click="emit('close')"
      >
        ✕
      </button>
    </div>

    <div class="flex flex-col gap-3 p-3 overflow-y-auto grow">
      <label class="flex flex-col gap-1">
        <span class="text-[11px] text-base-content/60">Питання</span>
        <input
          ref="questionInput"
          v-model="question"
          type="text"
          maxlength="120"
          placeholder="Що дивимось далі?"
          class="input-bordered w-full h-8 text-sm input input-sm"
          @keydown="onKey"
        />
      </label>

      <div class="flex flex-col gap-1.5">
        <span class="text-[11px] text-base-content/60">Варіанти</span>

        <div
          v-for="(option, index) in options"
          :key="index"
          class="flex items-center gap-1"
        >
          <input
            v-model="options[index]"
            type="text"
            maxlength="60"
            :placeholder="`Варіант ${index + 1}`"
            class="flex-1 input-bordered h-8 text-sm input input-sm"
            @keydown="onKey"
          />
          <button
            v-if="options.length > 2"
            class="text-base-content/40 hover:text-error px-1"
            title="Прибрати варіант"
            @click="dropOption(index)"
          >
            ✕
          </button>
        </div>

        <button
          v-if="options.length < MAX_OPTIONS"
          class="btn btn-ghost btn-xs self-start"
          @click="addOption"
        >
          + Ще варіант
        </button>
      </div>

      <div class="flex flex-col gap-1.5">
        <span class="text-[11px] text-base-content/60">Скільки триває</span>
        <div class="flex flex-wrap gap-1">
          <button
            v-for="item in DURATIONS"
            :key="item.seconds"
            type="button"
            class="px-2 py-1 rounded text-[11px] transition-colors"
            :class="
              seconds === item.seconds
                ? 'bg-primary text-primary-content'
                : 'bg-base-200 hover:bg-base-100 text-base-content/70'
            "
            @click="seconds = item.seconds"
          >
            {{ item.label }}
          </button>
        </div>
      </div>

      <p
        v-if="failure"
        class="text-[11px] text-error"
      >
        {{ failure }}
      </p>
    </div>

    <div class="flex flex-shrink-0 gap-2 bg-base-200 p-2 border-base-content/10 border-t">
      <button
        class="flex-1 btn btn-primary btn-sm"
        :disabled="!canSubmit"
        @click="submit"
      >
        Запустити
      </button>
      <button
        class="btn btn-ghost btn-sm"
        @click="emit('close')"
      >
        Скасувати
      </button>
    </div>
  </div>
</template>
